import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import CityAutocomplete from '../common/CityAutocomplete';
import TimeDatePickerModal from '../common/TimeDatePickerModal';
import AutocompleteInput from '../common/AutocompleteInput';
import AutocompleteTextarea from '../common/AutocompleteTextarea';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import './BusinessDashboard.css';

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

export default function BusinessDashboard() {
  const { currentUser, currentRole } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({
    totalJobs: 0,
    openJobs: 0,
    closedJobs: 0,
    pendingRequests: 0
  });
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobFormData, setJobFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    location: '',
    category: '',
    open_to_all: false,
    hourly_rate: '',
    day_rate: '',
    required_skills: '',
    required_certifications: '',
    expertise_level: '',
    mobile_required: false,
    equipment_required: false
  });
  const [savingJob, setSavingJob] = useState(false);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [loadingCompletedJobs, setLoadingCompletedJobs] = useState(false);
  const [editingPayAmount, setEditingPayAmount] = useState(null);
  const [newPayAmount, setNewPayAmount] = useState('');
  const [ongoingJobs, setOngoingJobs] = useState([]);
  const [loadingOngoingJobs, setLoadingOngoingJobs] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showDateTimeRangeModal, setShowDateTimeRangeModal] = useState(false);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [expandedStats, setExpandedStats] = useState(false);
  const jobFormRef = useRef(null);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (currentRole && currentRole !== 'business' && currentRole !== 'employer') {
      navigate('/freelancer/dashboard');
      return;
    }

    const loadData = async () => {
      try {
        const token = await currentUser.getIdToken();

        // Load job categories
        const categoriesRes = await fetch(`${API_BASE_URL}/api/job-categories`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (categoriesRes.ok) {
          const categoriesJson = await categoriesRes.json();
          setCategories(categoriesJson.categories || []);
        }

        // Load this business's job offers
        const jobsRes = await fetch(`${API_BASE_URL}/api/jobs?role=business`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        let jobsList = [];
        if (jobsRes.ok) {
          const json = await jobsRes.json();
          jobsList = json.jobs || [];
        }

        // Load unread / pending chat info
        const unreadRes = await fetch(`${API_BASE_URL}/api/chat/unread-count`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        let pendingRequests = 0;
        if (unreadRes.ok) {
          const json = await unreadRes.json();
          pendingRequests = json.pendingRequests || 0;
        }

        const totalJobs = jobsList.length;
        const openJobs = jobsList.filter((j) => (j.status || '').toLowerCase() === 'open').length;
        const closedJobs = jobsList.filter((j) => (j.status || '').toLowerCase() !== 'open').length;

        setJobs(jobsList);
        setStats({
          totalJobs,
          openJobs,
          closedJobs,
          pendingRequests
        });
      } catch (e) {
        console.error('Failed to load business dashboard', e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    loadCompletedJobs();
    loadOngoingJobs();
    loadBusinessProfile();
    loadUserProfile();
  }, [currentUser, currentRole, navigate]);

  // Check if user has admin role via custom claims
  useEffect(() => {
    const checkAdmin = async () => {
      if (!currentUser) return;
      try {
        let tr = await currentUser.getIdTokenResult();
        const hasAdminFromClaims = (() => {
          const claims = tr?.claims || {};
          const roles = claims.roles;
          if (Array.isArray(roles)) {
            return roles.map(String).map((r) => r.toLowerCase()).includes('admin');
          }
          if (typeof roles === 'string') {
            return roles.toLowerCase() === 'admin';
          }
          return !!claims.admin;
        })();
        if (!hasAdminFromClaims) {
          // Force refresh in case claims were just updated
          tr = await currentUser.getIdTokenResult(true);
          const claims = tr?.claims || {};
          const roles = claims.roles;
          const refreshedAdmin =
            (Array.isArray(roles) && roles.map(String).map((r) => r.toLowerCase()).includes('admin')) ||
            (typeof roles === 'string' && roles.toLowerCase() === 'admin') ||
            !!claims.admin;
          setIsAdmin(refreshedAdmin);
        } else {
          setIsAdmin(hasAdminFromClaims);
        }
      } catch (e) {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [currentUser]);

  const loadOngoingJobs = async () => {
    if (!currentUser) return;
    setLoadingOngoingJobs(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/chat/conversations`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) {
        throw new Error('Failed to load conversations');
      }
      const json = await res.json();
      const conversations = json.conversations || [];

      // Ongoing jobs are conversations that:
      // - are not fully completed
      // - have an attached job or assignment info
      const mapped = conversations
        .filter((conv) => {
          const status = (conv.status || '').toLowerCase();
          const isCompleted = status === 'completed';
          const hasJob = !!(conv.job || conv.jobId);
          return !isCompleted && hasJob;
        })
        .map((conv) => {
          const status = conv.status || 'accepted';
          const wpStatus = conv.workProposal?.status;
          const completeF = !!conv.completeFreelancer;
          const completeB = !!conv.completeBusiness;

          let phase = 'OPEN_CHAT';
          if (status === 'pending') phase = 'PENDING_ACCEPTANCE';
          else if ((completeF || completeB) && !(completeF && completeB)) {
            phase = 'COMPLETION_REQUESTED';
          } else if (wpStatus === 'confirmed') {
            phase = 'CONFIRMED';
          } else if (wpStatus === 'proposed') {
            phase = 'NEGOTIATING_SCHEDULE';
          }

          return {
            conversationId: conv.conversationId,
            jobTitle: conv.job?.title || conv.jobId || '',
            otherUserName:
              conv.otherUser?.displayName ||
              conv.otherUser?.email ||
              '',
            phase,
            raw: conv
          };
        });

      setOngoingJobs(mapped);
    } catch (e) {
      console.error('Failed to load ongoing jobs', e);
    } finally {
      setLoadingOngoingJobs(false);
    }
  };

  const loadCompletedJobs = async () => {
    if (!currentUser) return;
    setLoadingCompletedJobs(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/business/completed-jobs`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (res.ok) {
        const json = await res.json();
        setCompletedJobs(json.completed_jobs || []);
      }
    } catch (e) {
      console.error('Failed to load completed jobs', e);
    } finally {
      setLoadingCompletedJobs(false);
    }
  };

  const loadBusinessProfile = async () => {
    if (!currentUser) return;
    setLoadingProfile(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/business/profile/${currentUser.uid}/public`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (res.ok) {
        const json = await res.json();
        setBusinessProfile(json);
      }
    } catch (e) {
      console.error('Failed to load business profile', e);
    } finally {
      setLoadingProfile(false);
    }
  };

  const loadUserProfile = async () => {
    if (!currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        setUserProfile(userDoc.data());
      }
    } catch (e) {
      console.error('Failed to load user profile', e);
    }
  };

  if (!currentUser) return null;


  // Get displayName from Firestore (userProfile) first, fallback to Firebase Auth, then email
  const displayName =
    (userProfile && userProfile.displayName) ||
    currentUser.displayName ||
    (currentUser.email && currentUser.email.split('@')[0]) ||
    '';
  const handleCreateJob = () => {
    setEditingJob(null);
    setJobFormData({
      title: '',
      description: '',
      start_date: '',
      start_time: '',
      end_date: '',
      end_time: '',
      location: '',
      category: '',
      open_to_all: false,
      hourly_rate: '',
      day_rate: '',
      required_skills: '',
      required_certifications: '',
      expertise_level: '',
      mobile_required: false,
      equipment_required: false
    });
    setShowJobForm(true);

    // Scroll to form on mobile after a short delay to ensure DOM is updated
    setTimeout(() => {
      if (jobFormRef.current) {
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
          jobFormRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    }, 100);
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setJobFormData({
      title: job.title || '',
      description: job.description || '',
      start_date: job.start_date || '',
      start_time: job.start_time || '',
      end_date: job.end_date || '',
      end_time: job.end_time || '',
      location: job.location || '',
      category: job.category || '',
      open_to_all: job.open_to_all || false,
      hourly_rate: job.hourly_rate || '',
      day_rate: job.day_rate || '',
      required_skills: Array.isArray(job.required_skills) ? job.required_skills.join(', ') : (job.required_skills || ''),
      required_certifications: Array.isArray(job.required_certifications) ? job.required_certifications.join(', ') : (job.required_certifications || ''),
      expertise_level: job.expertise_level || '',
      mobile_required: job.mobile_required || false,
      equipment_required: job.equipment_required || false
    });
    setShowJobForm(true);
  };

  const handleSaveJob = async () => {
    if (!jobFormData.title || !jobFormData.start_date) {
      alert(t('businessDashboard.jobTitleAndDateRequired') || 'Job title and start date are required');
        return;
      }

    // Require at least one of hourly_rate or day_rate
    if (!jobFormData.hourly_rate && !jobFormData.day_rate) {
      alert(t('businessDashboard.payRequired') || t('jobs.payRequired') || 'Please enter at least an hourly rate or a day rate');
      return;
    }

    if (!jobFormData.open_to_all && !jobFormData.category) {
      alert(t('businessDashboard.categoryRequired') || 'Please select a category or enable "Open to everyone"');
      return;
    }

    // Require expertise_level
    if (!jobFormData.expertise_level) {
      alert(t('businessDashboard.expertiseLevelRequired') || 'Please select an expertise level');
      return;
    }

    setSavingJob(true);
    try {
      const token = await currentUser.getIdToken();

      const jobData = {
        title: jobFormData.title,
        description: jobFormData.description,
        start_date: jobFormData.start_date,
        start_time: jobFormData.start_time || null,
        end_date: jobFormData.end_date || null,
        end_time: jobFormData.end_time || null,
        location: jobFormData.location,
        category: jobFormData.category || null,
        open_to_all: jobFormData.open_to_all,
        hourly_rate: jobFormData.hourly_rate ? parseFloat(jobFormData.hourly_rate) : null,
        day_rate: jobFormData.day_rate ? parseFloat(jobFormData.day_rate) : null,
        required_skills: jobFormData.required_skills
          ? jobFormData.required_skills.split(',').map(s => s.trim()).filter(s => s)
          : [],
        required_certifications: jobFormData.required_certifications
          ? jobFormData.required_certifications.split(',').map(c => c.trim()).filter(c => c)
          : [],
        expertise_level: jobFormData.expertise_level || null,
        mobile_required: jobFormData.mobile_required || false,
        equipment_required: jobFormData.equipment_required || false
      };

      let res;
      if (editingJob) {
        res = await fetch(`${API_BASE_URL}/api/jobs/${editingJob.id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(jobData)
        });
      } else {
        res = await fetch(`${API_BASE_URL}/api/jobs`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(jobData)
        });
      }

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to save job');
      }

      // Save form values to localStorage for next time
      const saveLastValues = () => {
        try {
          localStorage.setItem('job_form_last_title', jobFormData.title || '');
          localStorage.setItem('job_form_last_description', jobFormData.description || '');
          localStorage.setItem('job_form_last_location', jobFormData.location || '');
          localStorage.setItem('job_form_last_category', jobFormData.category || '');
          localStorage.setItem('job_form_last_open_to_all', String(jobFormData.open_to_all));
          localStorage.setItem('job_form_last_hourly_rate', jobFormData.hourly_rate || '');
          localStorage.setItem('job_form_last_day_rate', jobFormData.day_rate || '');
          localStorage.setItem('job_form_last_required_skills', jobFormData.required_skills || '');
          localStorage.setItem('job_form_last_required_certifications', jobFormData.required_certifications || '');
          localStorage.setItem('job_form_last_expertise_level', jobFormData.expertise_level || '');
          localStorage.setItem('job_form_last_mobile_required', String(jobFormData.mobile_required));
          localStorage.setItem('job_form_last_equipment_required', String(jobFormData.equipment_required));
        } catch (e) {
          console.error('Error saving last form values:', e);
        }
      };
      saveLastValues();

      // Reload jobs
      const jobsRes = await fetch(`${API_BASE_URL}/api/jobs?role=business`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (jobsRes.ok) {
        const json = await jobsRes.json();
        setJobs(json.jobs || []);
        const openJobs = (json.jobs || []).filter((j) => (j.status || '').toLowerCase() === 'open').length;
        setStats(prev => ({
          ...prev,
          totalJobs: (json.jobs || []).length,
          openJobs
        }));
      }

      setShowJobForm(false);
      setEditingJob(null);
      // alert(t('businessDashboard.jobSaved') || 'Job saved successfully');
    } catch (e) {
      console.error('Failed to save job', e);
      alert(t('businessDashboard.failedToSaveJob') || `Failed to save job: ${e.message}`);
    } finally {
      setSavingJob(false);
    }
  };

  const handleCloseJob = async (jobId) => {
    if (!window.confirm(t('businessDashboard.confirmCloseJob') || 'Are you sure you want to close this job?')) {
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'closed' })
      });

      if (res.ok) {
        // Reload jobs
        const jobsRes = await fetch(`${API_BASE_URL}/api/jobs?role=business`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (jobsRes.ok) {
          const json = await jobsRes.json();
          setJobs(json.jobs || []);
          const openJobs = (json.jobs || []).filter((j) => (j.status || '').toLowerCase() === 'open').length;
          setStats(prev => ({
            ...prev,
            totalJobs: (json.jobs || []).length,
            openJobs
          }));
        }
      }
    } catch (e) {
      console.error('Failed to close job', e);
      alert(t('businessDashboard.failedToCloseJob') || 'Failed to close job');
    }
  };

  const handlePermanentlyDeleteJob = async (jobId) => {
    if (!window.confirm(t('businessDashboard.confirmPermanentlyDeleteJob') || 'Are you sure you want to permanently delete this job? This action cannot be undone.')) {
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        // Reload jobs
        const jobsRes = await fetch(`${API_BASE_URL}/api/jobs?role=business`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (jobsRes.ok) {
          const json = await jobsRes.json();
          setJobs(json.jobs || []);
          const openJobs = (json.jobs || []).filter((j) => (j.status || '').toLowerCase() === 'open').length;
          setStats(prev => ({
            ...prev,
            totalJobs: (json.jobs || []).length,
            openJobs
          }));
        }
      } else {
        const error = await res.json();
        alert(error.error || t('businessDashboard.failedToPermanentlyDeleteJob') || 'Failed to permanently delete job');
      }
    } catch (e) {
      console.error('Failed to permanently delete job', e);
      alert(t('businessDashboard.failedToPermanentlyDeleteJob') || 'Failed to permanently delete job');
    }
  };

  const handleEditPayAmount = (job) => {
    setEditingPayAmount(job.id);
    setNewPayAmount(job.pay_amount?.toString() || '');
  };

  const handleSavePayAmount = async (assignmentId) => {
    const amount = parseFloat(newPayAmount);
    if (isNaN(amount) || amount < 0) {
      alert(t('businessDashboard.invalidAmount') || 'Please enter a valid amount');
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/business/completed-jobs/${assignmentId}/pay-amount`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          new_amount: amount,
          confirm: true
        })
      });

      if (res.ok) {
        const json = await res.json();
        alert(json.message || t('businessDashboard.payAmountUpdated') || 'Pay amount updated successfully');
        setEditingPayAmount(null);
        setNewPayAmount('');
        loadCompletedJobs();
      } else {
        const error = await res.json();
        alert(error.error || t('businessDashboard.failedToUpdatePayAmount') || 'Failed to update pay amount');
      }
    } catch (e) {
      console.error('Failed to update pay amount', e);
      alert(t('businessDashboard.failedToUpdatePayAmount') || 'Failed to update pay amount');
    }
  };

  const handleCancelPayAmountEdit = () => {
    setEditingPayAmount(null);
    setNewPayAmount('');
  };

  const handleCancelPayAmountChange = async (assignmentId) => {
    if (!window.confirm(t('businessDashboard.confirmCancelPayChange') || 'Are you sure you want to cancel the pending pay amount change?')) {
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/business/completed-jobs/${assignmentId}/pay-amount-change`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (res.ok) {
        loadCompletedJobs();
      } else {
        const error = await res.json();
        alert(error.error || t('businessDashboard.failedToCancelPayChange') || 'Failed to cancel pay amount change');
      }
    } catch (e) {
      console.error('Failed to cancel pay amount change', e);
      alert(t('businessDashboard.failedToCancelPayChange') || 'Failed to cancel pay amount change');
    }
  };

  return (
    <div
      className={`business-dashboard-container ${isRTL ? 'rtl' : ''}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <header className="business-dashboard-header">
        <div className="business-dashboard-header-content">
          <div className="business-dashboard-header-left">
          </div>
          <div className="business-dashboard-title">
            <h1>{t('businessDashboard.title') || 'Business Dashboard'}</h1>
            <p className="business-dashboard-subtitle">
              {t('businessDashboard.subtitle') ||
                'Manage your job postings and connect with freelancers.'}
            </p>
          </div>
        </div>
      </header>

      <main className="business-dashboard-main">
        <section className="business-dashboard-hero">
          <h2 className="business-dashboard-greeting">
            {t('businessDashboard.greetingPrefix') || 'Hi'}{' '}
            <span className="business-dashboard-greeting-name">
              {displayName || currentUser.email}
            </span>
          </h2>
          <div className="business-dashboard-hero-stats">
            <div className="business-dashboard-hero-card">
              <div className="hero-card-label">
                {t('businessDashboard.openJobs') || 'Open job offers'}
              </div>
              <div className="hero-card-value">{stats.openJobs}</div>
            </div>
            <div className="business-dashboard-hero-card">
              <div className="hero-card-label">
                {t('businessDashboard.totalJobs') || 'Total job offers'}
              </div>
              <div className="hero-card-value">{stats.totalJobs}</div>
            </div>
            <div className="business-dashboard-hero-card">
              <div className="hero-card-label">
                {t('businessDashboard.pendingRequests') || 'Pending chat requests'}
              </div>
              <div className="hero-card-value">{stats.pendingRequests}</div>
            </div>
            {businessProfile && (
              <>
                <div className={`business-dashboard-hero-card hero-card-additional ${expandedStats ? 'expanded' : ''}`}>
                  <div className="hero-card-label">
                    {t('businessDashboard.level') || 'Level'}
                  </div>
                  <div className="hero-card-value">{businessProfile.level || 1}</div>
                </div>
                <div className={`business-dashboard-hero-card hero-card-additional ${expandedStats ? 'expanded' : ''}`}>
                  <div className="hero-card-label">
                    {t('businessDashboard.xp') || 'XP'}
                  </div>
                  <div className="hero-card-value">{businessProfile.xp || 0}</div>
                </div>
                <div className={`business-dashboard-hero-card hero-card-additional ${expandedStats ? 'expanded' : ''}`}>
                  <div className="hero-card-label">
                    {t('businessDashboard.rating') || 'Rating'}
                  </div>
                  <div className="hero-card-value">
                    {businessProfile.rating_overall?.toFixed(1) || '0.0'} ★
                  </div>
                </div>
                <div className={`business-dashboard-hero-card hero-card-additional ${expandedStats ? 'expanded' : ''}`}>
                  <div className="hero-card-label">
                    {t('businessDashboard.reviews') || 'Reviews'}
                  </div>
                  <div className="hero-card-value">{businessProfile.reviews_count || 0}</div>
                </div>
              </>
            )}
          </div>
          {businessProfile && (
            <button
              className="hero-stats-expand-button"
              onClick={() => setExpandedStats(!expandedStats)}
              aria-label={expandedStats ? (t('common.collapse') || 'Collapse') : (t('common.expand') || 'Expand')}
            >
              {expandedStats ? (
                <>
                  {t('common.collapse') || 'Show Less'} <span className="expand-icon">▲</span>
                </>
              ) : (
                <>
                  {t('common.showMore') || 'Show More'} <span className="expand-icon">▼</span>
                </>
              )}
            </button>
          )}
        </section>

        <section className="business-dashboard-actions">
          <button
            className="business-dashboard-action-button primary"
            onClick={handleCreateJob}
          >
            {t('businessDashboard.createJob') || 'Create New Job Posting'}
          </button>
          <button
            className="business-dashboard-action-button"
            onClick={() => navigate('/profile')}
          >
            {t('businessDashboard.editProfile') || 'Update business profile'}
          </button>
          <button
            className="business-dashboard-action-button"
            onClick={() => navigate('/chat')}
          >
            {t('businessDashboard.viewChats') || 'View chats'}
          </button>
        </section>

        {showJobForm && (
          <section className="business-dashboard-job-form" ref={jobFormRef}>
            <div className="job-form-header">
              <h3>
                {editingJob
                  ? t('businessDashboard.editJob') || 'Edit Job Posting'
                  : t('businessDashboard.createJob') || 'Create New Job Posting'}
              </h3>
              <button
                className="job-form-close"
                onClick={() => {
                  setShowJobForm(false);
                  setEditingJob(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="job-form-body">
              <div className="job-form-field">
                <label>
                  {t('businessDashboard.jobTitle') || 'Job Title'} *
                </label>
                <AutocompleteInput
                  type="text"
                  value={jobFormData.title}
                  onChange={(value) =>
                    setJobFormData({ ...jobFormData, title: value })
                  }
                  storageKey="job_title"
                  placeholder={t('businessDashboard.jobTitlePlaceholder') || 'Enter job title'}
                />
              </div>
              <div className="job-form-field">
                <label>
                  {t('businessDashboard.jobDescription') || 'Description'}
                </label>
                <AutocompleteTextarea
                  value={jobFormData.description}
                  onChange={(value) =>
                    setJobFormData({ ...jobFormData, description: value })
                  }
                  storageKey="job_description"
                  placeholder={t('businessDashboard.jobDescriptionPlaceholder') || 'Enter job description'}
                  rows={4}
                />
              </div>
              <div className="job-form-field">
                <label>
                  {t('businessDashboard.dateRangeAndTimes') || 'Date Range & Times'} *
                </label>
                <button
                  type="button"
                  onClick={() => setShowDateTimeRangeModal(true)}
                  className="time-date-picker-trigger-button"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '0.9rem'
                  }}
                >
                  {jobFormData.start_date && jobFormData.end_date && jobFormData.start_time && jobFormData.end_time
                    ? `${jobFormData.start_date} - ${jobFormData.end_date} (${jobFormData.start_time} - ${jobFormData.end_time})`
                    : jobFormData.start_date && jobFormData.end_date
                    ? `${jobFormData.start_date} - ${jobFormData.end_date}`
                    : t('businessDashboard.selectDateRangeAndTimes') || 'Select Date Range & Times'}
                </button>
                <span className="job-form-hint" style={{ marginTop: '0.5rem', display: 'block', fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                  {t('businessDashboard.dateRangeHelpText') || 'Select a date range and set times once for all days in that range.'}
                </span>
              </div>
              <div className="job-form-field">
                <label>{t('businessDashboard.location') || 'Location'}</label>
                <CityAutocomplete
                  value={jobFormData.location}
                  onChange={(value) =>
                    setJobFormData({ ...jobFormData, location: value })
                  }
                  placeholder={t('businessDashboard.locationPlaceholder') || 'Enter location'}
                  inputId="business-job-location"
                />
              </div>
              <div className="job-form-field">
                <label>
                  <input
                    type="checkbox"
                    checked={jobFormData.open_to_all}
                    onChange={(e) =>
                      setJobFormData({ ...jobFormData, open_to_all: e.target.checked, category: e.target.checked ? '' : jobFormData.category })
                    }
                  />
                  {t('businessDashboard.openToEveryone') || 'Open to everyone (all categories)'}
                </label>
              </div>
              {!jobFormData.open_to_all && (
                <div className="job-form-field">
                  <label>
                    {t('businessDashboard.category') || 'Category'} *
                  </label>
                  <select
                    value={jobFormData.category}
                    onChange={(e) => {
                      const newCategory = e.target.value;
                      setJobFormData({ ...jobFormData, category: newCategory });
                      // Save to localStorage for autocomplete
                      if (newCategory) {
                        try {
                          const stored = localStorage.getItem('job_form_autocomplete_category');
                          const suggestions = stored ? JSON.parse(stored) : [];
                          const filtered = suggestions.filter(s => s !== newCategory);
                          const updated = [newCategory, ...filtered].slice(0, 10);
                          localStorage.setItem('job_form_autocomplete_category', JSON.stringify(updated));
                        } catch (e) {
                          console.error('Error saving category suggestion:', e);
                        }
                      }
                    }}
                  >
                    <option value="">
                      {t('businessDashboard.selectCategory') || 'Select a category'}
                    </option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="job-form-row">
                <div className="job-form-field">
                  <label>{t('businessDashboard.hourlyRate') || 'Hourly Rate'}</label>
                  <AutocompleteInput
                    type="number"
                    value={jobFormData.hourly_rate}
                    onChange={(value) =>
                      setJobFormData({ ...jobFormData, hourly_rate: value })
                    }
                    storageKey="hourly_rate"
                    placeholder={t('businessDashboard.hourlyRatePlaceholder') || '₪'}
                  />
                </div>
                <div className="job-form-field">
                  <label>{t('businessDashboard.dayRate') || 'Day Rate'}</label>
                  <AutocompleteInput
                    type="number"
                    value={jobFormData.day_rate}
                    onChange={(value) =>
                      setJobFormData({ ...jobFormData, day_rate: value })
                    }
                    storageKey="day_rate"
                    placeholder={t('businessDashboard.dayRatePlaceholder') || '₪'}
                  />
                </div>
              </div>
              <div className="job-form-field">
                <label>{t('businessDashboard.requiredSkills') || 'Required Skills (comma-separated)'}</label>
                <AutocompleteInput
                  type="text"
                  value={jobFormData.required_skills}
                  onChange={(value) =>
                    setJobFormData({ ...jobFormData, required_skills: value })
                  }
                  storageKey="required_skills"
                  placeholder={t('businessDashboard.requiredSkillsPlaceholder') || 'e.g., JavaScript, React, Python'}
                />
              </div>
              <div className="job-form-field">
                <label>
                  {t('businessDashboard.requiredCertifications') || 'Required Certifications (comma-separated)'}
                </label>
                <AutocompleteInput
                  type="text"
                  value={jobFormData.required_certifications}
                  onChange={(value) =>
                    setJobFormData({
                      ...jobFormData,
                      required_certifications: value
                    })
                  }
                  storageKey="required_certifications"
                  placeholder={t('businessDashboard.requiredCertificationsPlaceholder') || 'e.g., AWS Certified, PMP'}
                />
              </div>
              <div className="job-form-field">
                <label>
                  {t('businessDashboard.expertiseLevel') || 'Expertise Level'} *
                </label>
                <select
                  value={jobFormData.expertise_level}
                  onChange={(e) => {
                    const newLevel = e.target.value;
                    setJobFormData({ ...jobFormData, expertise_level: newLevel });
                    // Save to localStorage for autocomplete
                    if (newLevel) {
                      try {
                        const stored = localStorage.getItem('job_form_autocomplete_expertise_level');
                        const suggestions = stored ? JSON.parse(stored) : [];
                        const filtered = suggestions.filter(s => s !== newLevel);
                        const updated = [newLevel, ...filtered].slice(0, 10);
                        localStorage.setItem('job_form_autocomplete_expertise_level', JSON.stringify(updated));
                      } catch (e) {
                        console.error('Error saving expertise level suggestion:', e);
                      }
                    }
                  }}
                  required
                >
                  <option value="">
                    {t('businessDashboard.selectExpertiseLevel') || 'Select expertise level'}
                  </option>
                  <option value="beginner">
                    {t('businessDashboard.expertiseLevelBeginner') || 'Beginner'}
                  </option>
                  <option value="intermediate">
                    {t('businessDashboard.expertiseLevelIntermediate') || 'Intermediate'}
                  </option>
                  <option value="advanced">
                    {t('businessDashboard.expertiseLevelAdvanced') || 'Advanced'}
                  </option>
                  <option value="expert">
                    {t('businessDashboard.expertiseLevelExpert') || 'Expert'}
                  </option>
                </select>
              </div>
              <div className="job-form-field">
                <label>
                  <input
                    type="checkbox"
                    checked={jobFormData.mobile_required}
                    onChange={(e) =>
                      setJobFormData({ ...jobFormData, mobile_required: e.target.checked })
                    }
                  />
                  {t('businessDashboard.mobileRequired') || 'Freelancer needs to be mobile'}
                </label>
              </div>
              <div className="job-form-field">
                <label>
                  <input
                    type="checkbox"
                    checked={jobFormData.equipment_required}
                    onChange={(e) =>
                      setJobFormData({ ...jobFormData, equipment_required: e.target.checked })
                    }
                  />
                  {t('businessDashboard.equipmentRequired') || 'Freelancer needs to have equipment'}
                </label>
              </div>
              <div className="job-form-actions">
                <button
                  className="business-dashboard-action-button primary"
                  onClick={handleSaveJob}
                  disabled={savingJob}
                >
                  {savingJob
                    ? t('common.saving') || 'Saving...'
                    : t('common.save') || 'Save'}
                </button>
                <button
                  className="business-dashboard-action-button"
                  onClick={() => {
                    setShowJobForm(false);
                    setEditingJob(null);
                  }}
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
              </div>
            </div>
          </section>
          )}

        <section className="business-dashboard-ongoing-jobs">
          <div className="business-dashboard-jobs-header">
            <h3>{t('businessDashboard.ongoingJobs') || 'Ongoing jobs with freelancers'}</h3>
          </div>
          {loadingOngoingJobs ? (
            <p className="business-dashboard-empty">{t('common.loading')}</p>
          ) : ongoingJobs.length === 0 ? (
            <p className="business-dashboard-empty">
              {t('businessDashboard.noOngoingJobs') ||
                'When you confirm work and schedule with a freelancer, those jobs will appear here until you mark them as completed.'}
            </p>
          ) : (
            <ul className="business-dashboard-jobs-list">
              {ongoingJobs.map((job) => (
                <li key={job.conversationId} className="business-dashboard-job-item ongoing-job-item">
                  <div className="job-item-main">
                    <div className="job-item-title">
                      {job.jobTitle || t('businessDashboard.job') || 'Job'}
                    </div>
                    <div className="job-item-meta">
                      {job.otherUserName && (
                        <span>
                          {t('businessDashboard.freelancer') || 'Freelancer'}: {job.otherUserName}
                        </span>
                      )}
                      <span className={`job-item-phase phase-${job.phase.toLowerCase()}`}>
                        {job.phase === 'PENDING_ACCEPTANCE' &&
                          (t('jobPage.phasePending') || 'Waiting for confirmation')}
                        {job.phase === 'NEGOTIATING_SCHEDULE' &&
                          (t('jobPage.phaseNegotiating') || 'Negotiating schedule')}
                        {job.phase === 'CONFIRMED' &&
                          (t('jobPage.phaseConfirmed') || 'Job confirmed')}
                        {job.phase === 'COMPLETION_REQUESTED' &&
                          (t('jobPage.phaseCompletionRequested') || 'Completion requested')}
                        {job.phase === 'OPEN_CHAT' &&
                          (t('jobPage.phaseOpenChat') || 'Active chat')}
                      </span>
                    </div>
                  </div>
                  <div className="job-item-actions">
                    {job.raw?.otherUser?.uid && (
                      <button
                        className="business-dashboard-job-button"
                        onClick={() => navigate(`/freelancer/${job.raw.otherUser.uid}/public`)}
                      >
                        {t('jobs.viewProfile') || 'View freelancer'}
                      </button>
                    )}
                    <button
                      className="business-dashboard-job-button"
                      onClick={() => navigate(`/chat?conversationId=${job.conversationId}`)}
                    >
                      {t('businessDashboard.openChatForJob') || 'Open chat'}
                    </button>
                    <button
                      className="business-dashboard-job-button"
                      onClick={() => navigate(`/job/${job.conversationId}`)}
                    >
                      {t('businessDashboard.viewJobDetails') || 'View job page'}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="business-dashboard-jobs">
          <div className="business-dashboard-jobs-header">
            <h3>{t('businessDashboard.yourJobPostings') || 'Your Job Postings'}</h3>
          </div>
          {loading ? (
            <p className="business-dashboard-empty">{t('common.loading')}</p>
          ) : jobs.length === 0 ? (
            <p className="business-dashboard-empty">
              {t('businessDashboard.noJobs') ||
                'You have no job postings yet. Create your first job posting to get started.'}
            </p>
          ) : (
            <ul className="business-dashboard-jobs-list">
              {jobs.map((job) => (
                <li key={job.id} className="business-dashboard-job-item">
                  <div className="job-item-main">
                    <div className="job-item-title">{job.title || t('jobs.jobTitle')}</div>
                    <div className="job-item-meta">
                      {job.start_date && (
                        <span>
                          {t('dashboard.jobStartDate')}: {job.start_date}
                        </span>
                      )}
                      {job.location && (
                        <span>
                          {t('dashboard.jobLocation')}: {job.location}
                        </span>
                      )}
                      {job.category && (
                        <span>
                          {t('businessDashboard.category')}: {job.category}
                        </span>
                      )}
                      {job.open_to_all && (
                        <span className="job-open-to-all">
                          {t('businessDashboard.openToEveryone') || 'Open to everyone'}
                        </span>
                      )}
                      <span className={`job-item-status status-${(job.status || 'open').toLowerCase()}`}>
                        {(job.status || 'open').toUpperCase()}
                      </span>
                    </div>
                    {(job.expertise_level || job.mobile_required || job.equipment_required) && (
                      <div className="job-item-requirements">
                        {job.expertise_level && (
                          <span className="job-item-badge">
                            <span className="job-item-badge-icon">🎯</span>
                            <span className="job-item-badge-text">
                              {t('jobs.expertiseLevel') || 'Expertise Level'}:{' '}
                              {t(`jobs.expertiseLevel${job.expertise_level.charAt(0).toUpperCase() + job.expertise_level.slice(1)}`) ||
                                job.expertise_level.charAt(0).toUpperCase() + job.expertise_level.slice(1)}
                            </span>
                          </span>
                        )}
                        {job.mobile_required && (
                          <span className="job-item-badge job-item-badge-soft">
                            <span className="job-item-badge-icon">🚗</span>
                            <span className="job-item-badge-text">
                              {t('jobs.mobileRequired') || 'Mobile Required'}
                            </span>
                          </span>
                        )}
                        {job.equipment_required && (
                          <span className="job-item-badge job-item-badge-soft">
                            <span className="job-item-badge-icon">🛠️</span>
                            <span className="job-item-badge-text">
                              {t('jobs.equipmentRequired') || 'Equipment Required'}
                            </span>
                          </span>
                        )}
                      </div>
                    )}
                    {job.description && (
                      <div className="job-item-description">{job.description}</div>
                    )}
                  </div>
                  <div className="job-item-actions">
                    {(job.status || 'open').toLowerCase() === 'open' && (
                      <>
                        <button
                          className="business-dashboard-job-button"
                          onClick={() => handleEditJob(job)}
                        >
                          {t('businessDashboard.edit') || 'Edit'}
                        </button>
                        <button
                          className="business-dashboard-job-button"
                          onClick={() => handleCloseJob(job.id)}
                        >
                          {t('businessDashboard.close') || 'Close'}
                        </button>
                      </>
                    )}
                    {(job.status || 'open').toLowerCase() !== 'open' && (
                      <button
                        className="business-dashboard-job-button delete"
                        onClick={() => handlePermanentlyDeleteJob(job.id)}
                      >
                        {t('businessDashboard.permanentlyDelete') || 'Permanently Delete'}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="business-dashboard-completed-jobs">
          <div className="business-dashboard-jobs-header">
            <h3>{t('businessDashboard.completedJobs') || 'Completed Jobs'}</h3>
          </div>
          {loadingCompletedJobs ? (
            <p className="business-dashboard-empty">{t('common.loading')}</p>
          ) : completedJobs.length === 0 ? (
            <p className="business-dashboard-empty">
              {t('businessDashboard.noCompletedJobs') || 'You have no completed jobs yet.'}
            </p>
          ) : (
            <ul className="business-dashboard-jobs-list">
              {completedJobs.map((job) => (
                <li
                  key={job.id}
                  className="business-dashboard-job-item completed-job-item"
                  onClick={() => job.conversation_id && navigate(`/job/${job.conversation_id}`)}
                  style={{ cursor: job.conversation_id ? 'pointer' : 'default' }}
                >
                  <div className="job-item-main">
                    <div className="job-item-title">{job.job_title || t('businessDashboard.job') || 'Job'}</div>
                    <div className="job-item-meta">
                      {job.freelancer?.displayName && (
                        <span>
                          {t('businessDashboard.freelancer') || 'Freelancer'}: {job.freelancer.displayName}
                        </span>
                      )}
                      {job.date && (
                        <span>
                          {t('businessDashboard.completedDate') || 'Completed'}: {new Date(job.date).toLocaleDateString()}
                        </span>
                      )}
                      {job.pending_pay_change && (
                        <span className="pending-pay-change-badge">
                          {t('businessDashboard.pendingPayChange') || 'Pending pay amount change'}
                        </span>
                      )}
                    </div>
                    {job.freelancer?.uid && (
                      <div className="job-item-meta" style={{ marginTop: '0.25rem' }}>
                        <button
                          className="business-dashboard-job-button"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/freelancer/${job.freelancer.uid}/public`);
                          }}
                        >
                          {t('jobs.viewProfile') || 'View freelancer'}
                        </button>
                      </div>
                    )}
                    <div className="pay-amount-section">
                      {editingPayAmount === job.id ? (
                        <div className="pay-amount-edit">
                          <input
                            type="number"
                            value={newPayAmount}
                            onChange={(e) => setNewPayAmount(e.target.value)}
                            placeholder={t('businessDashboard.enterAmount') || 'Enter amount'}
                            step="0.01"
                            min="0"
                          />
                          <div className="pay-amount-actions">
                            <button
                              className="business-dashboard-job-button"
                              onClick={() => handleSavePayAmount(job.id)}
                            >
                              {t('common.save') || 'Save'}
                            </button>
                            <button
                              className="business-dashboard-job-button"
                              onClick={handleCancelPayAmountEdit}
                            >
                              {t('common.cancel') || 'Cancel'}
                            </button>
                          </div>
                          {job.pending_pay_change && (
                            <div className="pending-change-info">
                              <p>
                                {t('businessDashboard.pendingChangeInfo') ||
                                  'A pay amount change is pending. Both parties must confirm.'}
                              </p>
                              {job.pending_pay_change.proposed_by === currentUser?.uid && (
                                <button
                                  className="business-dashboard-job-button"
                                  onClick={() => handleCancelPayAmountChange(job.id)}
                                >
                                  {t('businessDashboard.cancelPendingChange') || 'Cancel Pending Change'}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="pay-amount-display">
                          <span className="pay-amount-label">
                            {t('businessDashboard.payAmount') || 'Pay Amount'}:
                          </span>
                          <span className="pay-amount-value">₪{job.pay_amount?.toFixed(2) || '0.00'}</span>
                          {/* <button
                            className="business-dashboard-job-button"
                            onClick={() => handleEditPayAmount(job)}
                          >
                            {t('businessDashboard.editPayAmount') || 'Edit Amount'}
                          </button> */}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {businessProfile && businessProfile.reviews && businessProfile.reviews.length > 0 && (
          <section className="business-dashboard-reviews">
            <div className="business-dashboard-jobs-header">
              <h3>
                {t('businessDashboard.reviews') || 'Reviews'} ({businessProfile.reviews_count || 0})
              </h3>
            </div>
            <div className="business-dashboard-reviews-grid">
              {businessProfile.reviews.slice(0, 6).map((review) => (
                <div key={review.id} className="business-dashboard-review-card">
                  <div className="review-card-header">
                    <div className="review-rating">
                      {(review.rating_overall || 0).toFixed(1)} ★
                    </div>
                    <div className="review-date">
                      {review.created_at && review.created_at._seconds
                        ? new Date(review.created_at._seconds * 1000).toLocaleDateString()
                        : review.updated_at && review.updated_at._seconds
                        ? new Date(review.updated_at._seconds * 1000).toLocaleDateString()
                        : ''}
                    </div>
                  </div>
                  {review.review_text && (
                    <div className="review-text">{review.review_text}</div>
                  )}
                  <div className="review-details">
                    {review.rating_pays_on_time && (
                      <span className="review-detail-item">
                        {t('businessDashboard.paysOnTime') || 'Pays on time'}: {review.rating_pays_on_time.toFixed(1)} ★
                      </span>
                    )}
                    {review.rating_work_quality && (
                      <span className="review-detail-item">
                        {t('businessDashboard.workQuality') || 'Work quality'}: {review.rating_work_quality.toFixed(1)} ★
                      </span>
                    )}
                    {review.rating_experience && (
                      <span className="review-detail-item">
                        {t('businessDashboard.experience') || 'Experience'}: {review.rating_experience.toFixed(1)} ★
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {businessProfile.reviews_count > 6 && (
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <button
                  className="business-dashboard-action-button"
                  onClick={() => navigate(`/business/${currentUser.uid}/public`)}
                >
                  {t('businessDashboard.viewAllReviews') || 'View All Reviews'}
                </button>
              </div>
            )}
          </section>
        )}
        </main>
      <TimeDatePickerModal
        isOpen={showDateTimeRangeModal}
        onClose={() => setShowDateTimeRangeModal(false)}
        onConfirm={(result) => {
          setJobFormData({
            ...jobFormData,
            start_date: result.date || '',
            start_time: result.time || '',
            end_date: result.endDate || '',
            end_time: result.endTime || ''
          });
        }}
        mode="datetimerange"
        initialDate={jobFormData.start_date}
        initialEndDate={jobFormData.end_date}
        initialTime={jobFormData.start_time}
        initialEndTime={jobFormData.end_time}
        title={t('businessDashboard.selectDateRangeAndTimes') || 'Select Date Range & Times'}
      />
      </div>
    );
  }
